/**
 * Fall-stage callouts: one centered line where the countdown digit sits.
 * Day 1 and day 2 each have a default line. Stash milestones replace it.
 * Gems and shields do not.
 */

/** Same vertical anchor as `FallRenderer.drawCountdown` (`runHeight * 0.38`). */
export var FALL_CALLOUT_ANCHOR_Y = 0.38;

/** Matches `#fallMoveHint` fade so day 1 waits until that toast is gone. */
export var FALL_CALLOUT_HINT_FADE_MS = 280;

/** Beat after play starts when no control hint is on screen (day 2). */
export var FALL_CALLOUT_PLAY_BEAT_MS = 360;

export var FALL_CALLOUT_INTRO_MS = 240;
/** Fully faded in and fully scaled in before the fade-out starts. */
export var FALL_CALLOUT_SETTLE_MS = 500;
export var FALL_CALLOUT_OUTRO_MS = 180;
export var FALL_CALLOUT_HOLD_MS =
  FALL_CALLOUT_INTRO_MS + FALL_CALLOUT_SETTLE_MS + FALL_CALLOUT_OUTRO_MS;
export var FALL_DAY_CALLOUT_HOLD_MS = FALL_CALLOUT_HOLD_MS;
export var FALL_CATCH_CALLOUT_HOLD_MS = FALL_CALLOUT_HOLD_MS;

var FALL_DAY_CALLOUT_BY_CYCLE = {
  0: "Catch the creatures",
  1: "Dodge the boulders",
};

/**
 * One line per stash total this Fall. Counts reset when the next Fall starts.
 * @type {Array<{ count: number, text: string }>}
 */
export var FALL_CATCH_MILESTONES = [
  { count: 1, text: "Nice Catch" },
  { count: 10, text: "Amazing Catch" },
  { count: 20, text: "Awesome Catch" },
  { count: 30, text: "Incredible Catch" },
  { count: 40, text: "Legendary Catch" },
  { count: 50, text: "Mind-blowing Catch" },
];

var FALL_CALLOUT_SAMPLE_CHARS = 19;
var FALL_CALLOUT_CHAR_EM = 0.56;

/**
 * @param {number|null|undefined} cycleIndex
 * @returns {string|null}
 */
export function getFallDayCallout(cycleIndex) {
  if (cycleIndex == null || cycleIndex === "") return null;
  var idx = Math.floor(Number(cycleIndex));
  if (!isFinite(idx) || idx < 0) return null;
  return FALL_DAY_CALLOUT_BY_CYCLE[idx] || null;
}

/**
 * @param {boolean} movementHintOffered
 * @returns {number}
 */
export function getFallDayCalloutDelayMs(movementHintOffered) {
  return movementHintOffered ? FALL_CALLOUT_HINT_FADE_MS : FALL_CALLOUT_PLAY_BEAT_MS;
}

/**
 * Highest stash milestone crossed while going from `previousCount` to `nextCount`.
 * @param {number} previousCount
 * @param {number} nextCount
 * @returns {string|null}
 */
export function getFallCatchMilestoneCallout(previousCount, nextCount) {
  var prev = Math.max(0, Math.floor(Number(previousCount) || 0));
  var next = Math.floor(Number(nextCount) || 0);
  if (!isFinite(next) || next <= prev) return null;
  var text = null;
  var milestones = FALL_CATCH_MILESTONES;
  for (var i = 0; i < milestones.length; i++) {
    var count = milestones[i].count;
    if (count > prev && count <= next) text = milestones[i].text;
  }
  return text;
}

/**
 * Headline size that still fits the day-1 line across the play column.
 * @param {number} runWidth
 * @param {number} countdownPeakPx
 */
export function computeFallCalloutFontPx(runWidth, countdownPeakPx) {
  var width = Math.max(1, Number(runWidth) || 1);
  var peak = Number(countdownPeakPx);
  if (!isFinite(peak) || peak <= 0) peak = 72;
  var fitted = (width * 0.88) / (FALL_CALLOUT_SAMPLE_CHARS * FALL_CALLOUT_CHAR_EM);
  var capped = Math.min(fitted, peak * 0.46);
  return Math.max(16, Math.round(capped));
}

/**
 * @param {number} t 0–1
 * @param {number} overshoot
 * @returns {number} 0 at t=0, 1 at t=1, peaking above 1 in between.
 */
function easeOutBack(t, overshoot) {
  var c1 = overshoot;
  var c3 = c1 + 1;
  var u = t - 1;
  return 1 + c3 * u * u * u + c1 * u * u;
}

/**
 * Bounce in (overshoot, then rest), hold half a second, then grow while fading out.
 * @param {number} ageMs
 * @param {number} durationMs
 * @param {boolean} [reducedMotion]
 * @returns {{ alpha: number, scale: number, scaleX: number, scaleY: number, offsetY: number }|null}
 */
export function getFallCalloutPresentation(ageMs, durationMs, reducedMotion) {
  var age = Math.max(0, Number(ageMs) || 0);
  var duration = Math.max(1, Number(durationMs) || 1);
  if (age >= duration) return null;
  if (reducedMotion) {
    var fade = Math.min(160, duration / 3);
    var alphaStill = age < fade
      ? age / fade
      : (age > duration - fade ? (duration - age) / fade : 1);
    alphaStill = Math.max(0, Math.min(1, alphaStill));
    return { alpha: alphaStill, scale: 1, scaleX: 1, scaleY: 1, offsetY: 0 };
  }

  var intro = FALL_CALLOUT_INTRO_MS;
  var settleEnd = intro + FALL_CALLOUT_SETTLE_MS;
  if (age < intro) {
    var sprung = easeOutBack(age / intro, 3.2);
    var alphaIn = Math.min(1, age / 80);
    var scaleY = 0.35 + 0.65 * sprung;
    var scaleX = sprung <= 1
      ? 0.62 + 0.38 * sprung
      : 1 - (sprung - 1) * 0.55;
    var offsetY = 26 * (1 - sprung);
    return {
      alpha: alphaIn,
      scale: scaleY,
      scaleX: scaleX,
      scaleY: scaleY,
      offsetY: offsetY,
    };
  }
  if (age < settleEnd) {
    return { alpha: 1, scale: 1, scaleX: 1, scaleY: 1, offsetY: 0 };
  }
  var outroSpan = Math.max(1, duration - settleEnd);
  var outT = Math.max(0, Math.min(1, (age - settleEnd) / outroSpan));
  var grown = 1 + 0.46 * outT;
  return {
    alpha: 1 - outT,
    scale: grown,
    scaleX: grown,
    scaleY: grown,
    offsetY: 0,
  };
}

function makeCallout(text, kind, elapsed) {
  var hold = kind === "day" ? FALL_DAY_CALLOUT_HOLD_MS : FALL_CATCH_CALLOUT_HOLD_MS;
  return {
    text: text,
    kind: kind,
    startedAtMs: elapsed,
    endsAtMs: elapsed + hold,
  };
}

/**
 * @param {object|null} active
 * @param {object} input
 * @returns {{ active: object|null, dayCalloutShown: boolean, catchCount: number }}
 */
export function stepFallCallout(active, input) {
  var s = input || {};
  var elapsed = Math.max(0, Number(s.playElapsedMs) || 0);
  var dayShown = !!s.dayCalloutShown;
  var catchCount = Math.max(0, Math.floor(Number(s.catchCount) || 0));
  var catches = Math.max(0, Math.floor(Number(s.creatureCatches) || 0));
  if (!s.playing || s.blocked) {
    return { active: null, dayCalloutShown: dayShown, catchCount: catchCount };
  }

  var next = active && elapsed < active.endsAtMs ? active : null;
  var dayText = getFallDayCallout(s.cycleIndex);
  var delay = getFallDayCalloutDelayMs(!!s.movementHintOffered);
  var dayDue = !dayShown && !!dayText && !s.movementHintVisible && elapsed >= delay;

  if (catches > 0) {
    var milestone = getFallCatchMilestoneCallout(catchCount, catchCount + catches);
    catchCount += catches;
    if (milestone) {
      next = makeCallout(milestone, "catch", elapsed);
      dayShown = true;
    }
  }
  if (!dayShown && dayDue && !(next && next.kind === "catch")) {
    next = makeCallout(dayText, "day", elapsed);
    dayShown = true;
  }

  return { active: next, dayCalloutShown: dayShown, catchCount: catchCount };
}
